import { motion } from "framer-motion";
import { Anchor, Gamepad2, Swords, TrendingUp, Trophy } from "lucide-react";

const PHASES = [
  {
    icon: Anchor,
    phase: "Phase 1",
    title: "Splash Launch",
    status: "done",
    items: ["Fair launch on Pump.fun", "Liquidity burned", "Website + community live"],
  },
  {
    icon: Gamepad2,
    phase: "Phase 2",
    title: "Play & Earn",
    status: "done",
    items: ["3D jetski runner live", "Hold 1M to play", "Rewards + leaderboard"],
  },
  {
    icon: Swords,
    phase: "Phase 3",
    title: "Stake to Earn",
    status: "next",
    items: ["Lock $JETDOG, earn rewards", "Boosted APY for hodlers", "Auto on-chain payouts"],
  },
  {
    icon: TrendingUp,
    phase: "Phase 4",
    title: "Catch the Wave",
    status: "progress",
    items: ["DexScreener live chart ✓", "Trending on DexTools", "Meme army onboarding"],
  },
  {
    icon: Trophy,
    phase: "Phase 5",
    title: "Ocean Domination",
    status: "next",
    items: ["CMC + CoinGecko", "Staking seasons & rewards", "CEX listings + NFT drop"],
  },
];

const STATUS = {
  done: { label: "LIVE", cls: "bg-[rgba(57,255,20,0.14)] text-[var(--jd-green)] border-[rgba(57,255,20,0.45)]", dot: true },
  progress: { label: "IN PROGRESS", cls: "bg-[rgba(255,215,0,0.12)] text-[var(--jd-secondary)] border-[rgba(255,215,0,0.45)]", dot: false },
  next: { label: "UP NEXT", cls: "bg-white/5 text-[var(--jd-ocean)] border-white/20", dot: false },
};

export const Roadmap = () => {
  return (
    <section
      id="roadmap"
      className="section"
      style={{
        backgroundImage: "linear-gradient(180deg, rgba(5,5,10,0.85), rgba(5,5,10,0.93)), url('/game/gg-grid.jpg')",
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
      data-testid="roadmap-section"
    >
      <div className="mx-auto max-w-6xl">
        <div className="text-left md:text-center">
          <p className="font-accent text-2xl text-[var(--jd-accent)]">where we’re sailing</p>
          <h2 className="font-display font-bold text-4xl md:text-6xl uppercase mt-2 neon-title-pink" data-testid="roadmap-title">
            Roadmap
          </h2>
          <p className="mt-3 text-xs md:text-sm text-[var(--jd-ocean)] font-mono uppercase tracking-widest">
            3 of 5 phases already shipped — the dog doesn’t stop
          </p>
        </div>

        <div className="mt-14 relative">
          <svg
            className="absolute left-0 right-0 top-1/2 -translate-y-1/2 w-full h-12 hidden lg:block opacity-50"
            viewBox="0 0 1200 50"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <path
              d="M0 25 Q150 -10 300 25 T600 25 T900 25 T1200 25"
              fill="none"
              stroke="rgba(0,229,255,0.4)"
              strokeWidth="2"
              strokeDasharray="8 8"
            />
          </svg>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 md:gap-5 relative">
            {PHASES.map((p, i) => {
              const Icon = p.icon;
              const st = STATUS[p.status];
              return (
                <motion.div
                  key={p.phase}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className="gg-panel rounded-2xl p-5 card-lift"
                  data-testid={`roadmap-phase-${i + 1}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div
                      className="w-11 h-11 rounded-xl border border-white/10 flex items-center justify-center shrink-0"
                      style={{ color: i % 2 === 0 ? "var(--jd-secondary)" : "var(--jd-primary)", background: "rgba(255,255,255,0.03)" }}
                    >
                      <Icon size={20} />
                    </div>
                    <span
                      className={`rounded-full border px-2 py-0.5 font-mono font-bold text-[10px] tracking-wider inline-flex items-center gap-1.5 ${st.cls}`}
                      data-testid={`roadmap-status-${i + 1}`}
                    >
                      {st.dot && <span className="w-1.5 h-1.5 rounded-full bg-[var(--jd-green)] badge-live-dot" />}
                      {st.label}
                    </span>
                  </div>
                  <p className="mt-3 font-accent text-xl text-[var(--jd-accent)]">{p.phase}</p>
                  <h3 className="font-display font-bold text-lg uppercase leading-tight mt-1">{p.title}</h3>
                  <ul className="mt-3 space-y-1.5 text-sm text-[var(--jd-ocean)]">
                    {p.items.map((it) => (
                      <li key={it} className="flex gap-2">
                        <span className={p.status === "done" ? "text-[var(--jd-green)]" : "text-[var(--jd-primary)]"}>
                          {p.status === "done" ? "✓" : "→"}
                        </span>
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Roadmap;
